/* ======================================================
   Cart drawer recommendation "Select a size" popup.
   A compact bottom-sheet, rendered as a direct child of <body>
   at runtime so it stays viewport-fixed on every browser (see
   connectedCallback below for why).

   Every recommendation card gets its own instance of this
   element, each with its own popup. Only one should ever be
   open at a time, so opening one closes whichever other one
   was already open instead of letting them stack.
   ====================================================== */
let openSizeSelectInstance = null;

class CartDrawerSizeSelect extends HTMLElement {
  constructor() {
    super();
    this.openBtn = this.querySelector('[data-size-popup-open]');
    this.popup = this.querySelector('[data-size-popup]');
    this.closeBtn = this.querySelector('[data-size-popup-close]');
    this.options = Array.from(this.querySelectorAll('[data-size-popup-option]'));
    this.submitBtn = this.querySelector('[data-size-popup-submit]');
    this.errorEl = this.querySelector('[data-size-popup-error]');
    this.selectedVariantId = null;
    this.submitLabel = this.submitBtn ? this.submitBtn.textContent : '';
    // On the cart drawer's own recommendation cards the drawer is already
    // open, so a successful add just needs to refresh it in place. Used
    // from collection/grid cards (where the drawer isn't open yet), this
    // attribute makes a successful add also open the drawer so the
    // customer sees what they just added — matching a normal add-to-cart.
    this.openCartOnSuccess = this.hasAttribute('data-open-cart-on-success');
    // When set, there's no separate "Add to Bag" submit button — tapping a
    // size adds it immediately. Used by the collection/grid card popup;
    // the cart drawer's own recommendation popup leaves this unset and
    // keeps its separate confirm step.
    this.instantAdd = this.hasAttribute('data-instant-add');
    // Remember which size options started out unavailable, so that once an
    // instant-add request finishes we only re-enable the ones that were
    // actually selectable to begin with.
    this.originallyDisabledOptions = new Set(this.options.filter((o) => o.disabled));

    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  connectedCallback() {
    if (this.openBtn) this.openBtn.addEventListener('click', this.handleOpen);
    if (this.closeBtn) this.closeBtn.addEventListener('click', this.handleClose);
    if (this.popup) this.popup.addEventListener('click', this.handleBackdropClick);
    this.options.forEach((option) => {
      option.addEventListener('click', () => this.handleSelect(option));
    });
    if (this.submitBtn) this.submitBtn.addEventListener('click', this.handleSubmit);

    // iOS Safari doesn't treat `position: fixed` descendants of a scrolling
    // (`overflow-y: auto`) ancestor as fixed to the viewport - it ties them to
    // that scroll container instead, so the popup never becomes visible there
    // (Android/Chrome doesn't have this quirk). The cart drawer's scrollable
    // body is exactly such an ancestor, so move the popup out to <body> to get
    // consistent, true viewport-fixed positioning on every browser.
    if (this.popup && document.body) {
      document.body.appendChild(this.popup);
    }
  }

  disconnectedCallback() {
    if (this.openBtn) this.openBtn.removeEventListener('click', this.handleOpen);
    if (this.closeBtn) this.closeBtn.removeEventListener('click', this.handleClose);
    if (this.popup) this.popup.removeEventListener('click', this.handleBackdropClick);
    if (this.submitBtn) this.submitBtn.removeEventListener('click', this.handleSubmit);
    // Since the popup was relocated to <body> in connectedCallback, it won't
    // be cleaned up automatically when the cart drawer section re-renders.
    // Remove it ourselves to avoid leaving orphaned popups behind.
    if (this.popup && this.popup.parentElement === document.body) {
      this.popup.remove();
    }
    // Don't leave a dangling reference if this instance's section
    // re-renders (and this element is torn down) while its popup
    // was the currently-open one.
    if (openSizeSelectInstance === this) {
      openSizeSelectInstance = null;
    }
  }

  handleOpen(e) {
    e.preventDefault();
    // Close whichever other popup is currently open before opening
    // this one, so they never stack.
    if (openSizeSelectInstance && openSizeSelectInstance !== this) {
      openSizeSelectInstance.handleClose();
    }
    if (this.popup) this.popup.hidden = false;
    openSizeSelectInstance = this;
  }

  handleClose() {
    if (this.popup) this.popup.hidden = true;
    if (openSizeSelectInstance === this) {
      openSizeSelectInstance = null;
    }
  }

  handleBackdropClick(e) {
    if (e.target === this.popup) this.handleClose();
  }

  handleSelect(option) {
    if (option.disabled) return;
    this.options.forEach((o) => o.classList.remove('is-selected'));
    option.classList.add('is-selected');
    this.selectedVariantId = option.getAttribute('data-variant-id');
    if (this.submitBtn) this.submitBtn.disabled = false;
    if (this.errorEl) this.errorEl.hidden = true;

    if (this.instantAdd) {
      this.handleSubmit();
    }
  }

  async handleSubmit() {
    if (!this.selectedVariantId) return;

    if (this.submitBtn) {
      this.submitBtn.disabled = true;
      this.submitBtn.textContent = (window.wetheme && window.wetheme.translations && window.wetheme.translations.loading) || this.submitLabel;
    }
    if (this.instantAdd) {
      this.options.forEach((o) => {
        o.disabled = true;
      });
    }
    if (this.errorEl) this.errorEl.hidden = true;

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: this.selectedVariantId, quantity: 1, sections: ['cart-drawer'] }),
      });
      const responseJson = await response.json();

      if (response.ok) {
        window.eventBus.emit('update:cart:drawer', responseJson);
        if (this.openCartOnSuccess) {
          window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
        }
        this.handleClose();
      } else if (this.errorEl) {
        this.errorEl.textContent = responseJson.description || responseJson.message || 'Unable to add this item to the bag.';
        this.errorEl.hidden = false;
      }
    } catch (err) {
      console.error('Unable to add to cart: ', err);
      if (this.errorEl) {
        this.errorEl.textContent = 'Unable to add this item to the bag.';
        this.errorEl.hidden = false;
      }
    } finally {
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = this.submitLabel;
      }
      if (this.instantAdd) {
        this.options.forEach((o) => {
          if (!this.originallyDisabledOptions.has(o)) o.disabled = false;
        });
      }
    }
  }
}

customElements.define('cart-drawer-size-select', CartDrawerSizeSelect);